"use client";

import AutoOrderDescription from "@/app/ec/product/[basePath]/auto-order/autoOrderDescription";
import AutoOrderPopover from "@/app/ec/product/[basePath]/auto-order/autoOrderPopover";
import { AutoOrderType, getSelectedAutoOrderType } from "@/app/ec/product/callToAction";
import Modal from "@/components/design-system/modal";
import { OrderPlacementSchedule, useGetSubscriptionFutureDatesQuery } from "@/store/autoOrderApi";
import { useGetUserQuery } from "@/store/userApi";
import { useWindowWidth } from "@react-hook/window-size";
import dayjs from "dayjs";
import CalendarSVG from "public/next-public/common/icons/uil-calendar.svg";
import FastShippingIcon from "public/next-public/common/icons/fast-shipping-icon.svg";
import { FunctionComponent, useEffect, useState } from "react";
import styles from "./autoOrderMessageBox.module.scss";

const AutoOrderMessageBox: FunctionComponent<AutoOrderMessageBoxProps> = ({
  autoOrderPopover,
  selectedAutoOrder,
  subscriptionFutureDates,
}) => {
  const [mobileDetailsVisible, setMobileDetailsVisible] = useState(false);
  const [mobileFrequecyDetailsVisible, setMobileFrequecyDetailsVisible] = useState(false);
  const screenWidth = useWindowWidth();
  const { data: user } = useGetUserQuery();
  const isSignedIn = user?.IsGuestUser === false && user?.isAuthorized === true;

  useEffect(() => {
    window.addEventListener("resize", onResize);

    // The following cleans up the resize attachment
    return () => {
      window.removeEventListener("resize", onResize);
    };
  });

  const onResize = () => {
    if (mobileDetailsVisible == true) {
      setMobileDetailsVisible(false);
    }

    if (mobileFrequecyDetailsVisible == true) {
      setMobileFrequecyDetailsVisible(false);
    }
  };

  const dateDetailMessage = (() => {
    if (!selectedAutoOrder || !Array.isArray(subscriptionFutureDates)) {
      return null;
    }

    const selectedIndex = getSelectedAutoOrderType(selectedAutoOrder).indexServer;
    const subscriptionDates = subscriptionFutureDates[selectedIndex];

    if (!subscriptionDates || !Array.isArray(subscriptionDates.FutureOrderDates)) {
      return null;
    }

    return (
      <>
        <section>
          <ul className={styles["auto-order-date-list"]}>
            {subscriptionDates.FutureOrderDates.map((date, index) => {
              return (
                <li key={index}>
                  <span className={styles["fast-shipping-icon"]}>
                    <FastShippingIcon width={24} height={28} aria-hidden />
                  </span>
                  {date}
                </li>
              );
            })}
          </ul>
        </section>
        <section>
          <p className={styles["tooltip-paragraph"]}>
            This auto order will be placed every {getSelectedAutoOrderType(selectedAutoOrder).description}{" "}
            {getSelectedAutoOrderType(selectedAutoOrder).frequencyText} of the month.
          </p>
        </section>
      </>
    );
  })();

  // Projected next-order date for a brand-new subscription, from the modern
  // users-service endpoint (mirrors addToAutoOrderModal's usage). That endpoint
  // is authenticated, so signed-out visitors read the projection out of the
  // per-product dates the PDP already fetched server-side (SPR-2885).
  const selectedFrequencyId =
    selectedAutoOrder !== AutoOrderType.ONE_TIME.index
      ? (getSelectedAutoOrderType(selectedAutoOrder).frequenceId as OrderPlacementSchedule)
      : undefined;

  const { data: futureDatesByFrequency, isLoading: isFutureDatesLoading } = useGetSubscriptionFutureDatesQuery(
    undefined,
    {
      skip: !selectedFrequencyId || !isSignedIn,
    },
  );

  const nextPurchaseDate = (() => {
    if (!selectedFrequencyId) {
      return "";
    }

    if (!isSignedIn) {
      const projectedDates = Array.isArray(subscriptionFutureDates)
        ? subscriptionFutureDates[getSelectedAutoOrderType(selectedAutoOrder).indexServer]?.FutureOrderDates
        : undefined;
      const projectedDate = Array.isArray(projectedDates) ? projectedDates[0] : undefined;

      return projectedDate && dayjs(projectedDate).isValid() ? dayjs(projectedDate).format("MM/DD/YYYY") : "";
    }

    const dates = futureDatesByFrequency?.[selectedFrequencyId];

    return dates && dates.length > 0 ? dayjs(dates[0]).format("MM/DD/YYYY") : "";
  })();

  // Avoids rendering "Your first order will be placed on ." while the date is
  // loading or unavailable (e.g. request failed).
  const firstOrderDateMessage = (() => {
    if (isFutureDatesLoading) {
      return "";
    }

    if (!nextPurchaseDate) {
      return null;
    }

    return (
      <>
        Your first order will be placed on <span className={styles.nextPurchaseDate}>{nextPurchaseDate}.</span>
      </>
    );
  })();

  const autoOrderDetailsModal = (() => {
    return (
      <Modal show={mobileDetailsVisible} size="skinny" onHide={() => setMobileDetailsVisible(false)} title="Auto Order">
        <div className={styles["auto-order-info-modal-body"]}>
          <AutoOrderDescription />
        </div>
      </Modal>
    );
  })();

  const frequentPurchaseMessageWithModal = (() => {
    return (
      <>
        {selectedAutoOrder && (
          <>
            <div className={styles["first-order-msg-container"]}>
              <span className={styles["calendar-icon-column"]}>
                <CalendarSVG className={styles["calendar-icon"]} />
              </span>
              <span className={styles["message-column"]}>
                {firstOrderDateMessage}
                <br /> We&apos;ll send you a reminder beforehand. Cancel anytime.
              </span>
            </div>
            {autoOrderDetailsModal}

            <Modal
              show={mobileFrequecyDetailsVisible}
              size="skinny"
              onHide={() => setMobileFrequecyDetailsVisible(false)}
              title="Your next auto orders will be on"
            >
              <div className={styles["auto-order-date-info-modal-body"]}>{dateDetailMessage}</div>
            </Modal>
          </>
        )}
      </>
    );
  })();
  /**
   * This function will determine if the page is using a desktop layout.  As of writing this is using
   * bootstrap's default "XL" size to determine this, as well as accounts for a slight differnce in layout
   * referenced in productDetailPage.scss dealing with .add-to-cart-cta (at line 505 as of writing).
   *
   * If the values here are changed, they need to be changed in that file as well.
   * @param width The width of the current browser window
   * @returns boolean
   */
  const isCTAUsingDesktopLayout = (width: number): boolean => {
    if (width >= 1200 && width <= 1400) {
      return false;
    } else if (width > 1200) {
      return true;
    } else {
      return false;
    }
  };
  /**
   * Returns either a version of <AutoOrderPopover /> controlled by the parent if the desktop layout
   * is in use, or one that is controlled by itself if not.
   */
  const chosenAutoOrderPopover =
    isCTAUsingDesktopLayout(screenWidth) === true ? autoOrderPopover : <AutoOrderPopover />;

  return (
    <>
      <div className={`${styles["auto-order-message-box-info"]} ${styles["mouse-visible"]}`}>
        {selectedAutoOrder === AutoOrderType.ONE_TIME.index && (
          <span>Do you order regularly? Try {chosenAutoOrderPopover} and let Net32 place orders for you!</span>
        )}
        {selectedAutoOrder !== AutoOrderType.ONE_TIME.index && (
          <div className={styles["first-order-msg-container"]}>
            <span className={styles["calendar-icon-column"]}>
              <CalendarSVG className={styles["calendar-icon"]} />
            </span>
            <span className={styles["message-column"]}>
              {firstOrderDateMessage}
              We&apos;ll send you a reminder beforehand. Cancel anytime.
            </span>
          </div>
        )}
      </div>
      <div className={`${styles["auto-order-message-box-info"]}  ${styles["touch-visible"]}`}>
        {selectedAutoOrder === AutoOrderType.ONE_TIME.index && (
          <>
            <span>
              Do you order regularly? Try <a onClick={() => setMobileDetailsVisible(true)}>Auto Order</a> and let Net32
              place orders for you!
            </span>
            {autoOrderDetailsModal}
          </>
        )}
        {selectedAutoOrder !== AutoOrderType.ONE_TIME.index && <>{frequentPurchaseMessageWithModal}</>}
      </div>
    </>
  );
};

type AutoOrderMessageBoxProps = {
  selectedAutoOrder: number;
  subscriptionFutureDates: SubscriptionFutureDatesForProduct[];
  autoOrderPopover: React.ReactNode;
};

export default AutoOrderMessageBox;
